import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { auditApi, staffApi } from '@/lib/api';
import { useAuthStore } from '@/hooks/useAuthStore';

interface AuditLog {
  id: string;
  staffId: string;
  staffEmail?: string;
  staffNombre?: string;
  action: string;
  targetType: string;
  targetId: string | null;
  metadata: Record<string, unknown> | null;
  ipAddress: string | null;
  userAgent: string | null;
  severity: 'INFO' | 'WARNING' | 'CRITICAL';
  hash: string;
  timestamp: string;
}

interface StaffMember {
  id: string;
  email: string;
  nombre: string;
}

// C12 R1 — severityColors must match backend AuditSeverity enum (INFO/WARNING/CRITICAL).
// Previously (pre-C12) this map had LOW/MEDIUM/HIGH which never matched any
// backend emission, so badges always fell through to the neutral fallback.
const severityColors: Record<string, string> = {
  INFO: 'bg-gray-100 text-gray-800',
  WARNING: 'bg-yellow-100 text-yellow-800',
  CRITICAL: 'bg-red-100 text-red-800',
};

const actionColors: Record<string, string> = {
  LOGIN: 'bg-green-100 text-green-800',
  LOGOUT: 'bg-gray-100 text-gray-800',
  LOGIN_FAILED: 'bg-red-100 text-red-800',
  VIEW: 'bg-blue-100 text-blue-800',
  CREATE: 'bg-emerald-100 text-emerald-800',
  UPDATE: 'bg-yellow-100 text-yellow-800',
  DELETE: 'bg-red-100 text-red-800',
  EXPORT: 'bg-purple-100 text-purple-800',
  SUSPEND: 'bg-orange-100 text-orange-800',
  REACTIVATE: 'bg-teal-100 text-teal-800',
};

const ITEMS_PER_PAGE = 25;

export default function Audit() {
  const { staff: currentUser } = useAuthStore();
  const isSuperAdmin = currentUser?.role === 'SUPER_ADMIN';
  const queryClient = useQueryClient();

  // Filters
  const [filters, setFilters] = useState({
    action: '',
    severity: '',
    staffId: '',
    targetType: '',
    fromDate: '',
    toDate: '',
  });
  const [page, setPage] = useState(0);

  // Fetch audit logs
  const { data: logsData, isLoading: logsLoading, error: logsError } = useQuery({
    queryKey: ['auditLogs', filters, page],
    queryFn: () =>
      auditApi.getLogs({
        ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)),
        limit: ITEMS_PER_PAGE,
        offset: page * ITEMS_PER_PAGE,
      }),
  });

  // Fetch available actions/severities
  const { data: actionsData } = useQuery({
    queryKey: ['auditActions'],
    queryFn: auditApi.getActions,
  });

  // Fetch staff for filter dropdown
  const { data: staffData } = useQuery({
    queryKey: ['staffList'],
    queryFn: () => staffApi.getAll({ limit: 100 }),
  });

  // Verify integrity mutation
  const verifyMutation = useMutation({
    mutationFn: () => auditApi.verifyIntegrity({
      fromDate: filters.fromDate || undefined,
      toDate: filters.toDate || undefined,
    }),
  });

  const logs: AuditLog[] = logsData?.data?.logs || [];
  const total = logsData?.data?.total || 0;
  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);
  const actions: string[] = actionsData?.data?.actions || [];
  const severities: string[] = actionsData?.data?.severities || [];
  const staffList: StaffMember[] = staffData?.data?.staff || [];

  // Stats
  // C12 R1/R2 — severity filter WARNING (backend enum), date field timestamp.
  const stats = {
    total,
    critical: logs.filter((l) => l.severity === 'CRITICAL').length,
    warnings: logs.filter((l) => l.severity === 'WARNING').length,
    today: logs.filter((l) => {
      const logDate = new Date(l.timestamp).toDateString();
      return logDate === new Date().toDateString();
    }).length,
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const clearFilters = () => {
    setFilters({
      action: '',
      severity: '',
      staffId: '',
      targetType: '',
      fromDate: '',
      toDate: '',
    });
    setPage(0);
    // C12 R6 — invalidate so the empty-filter list actually refetches even
    // when react-query deems the cached empty-filter key still fresh.
    queryClient.invalidateQueries({ queryKey: ['auditLogs'] });
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Log</h1>
          <p className="text-gray-600">Registro inmutable de actividad administrativa</p>
        </div>
        {isSuperAdmin && (
          <button
            onClick={() => verifyMutation.mutate()}
            disabled={verifyMutation.isPending}
            className="btn-secondary flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            {verifyMutation.isPending ? 'Verificando...' : 'Verificar Integridad'}
          </button>
        )}
      </div>

      {/* Integrity Result */}
      {verifyMutation.isSuccess && (
        <div className={`mb-4 p-4 rounded-lg border ${
          verifyMutation.data?.data?.valid
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          <div className="flex items-center gap-2">
            {verifyMutation.data?.data?.valid ? (
              <>
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium">Integridad verificada</span>
                <span className="text-sm">({verifyMutation.data?.data?.checkedCount} registros)</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span className="font-medium">Errores de integridad detectados</span>
                <span className="text-sm">({verifyMutation.data?.data?.errors?.length} errores)</span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="card p-4">
          <div className="text-sm text-gray-500">Total Registros</div>
          <div className="text-2xl font-bold text-gray-900">{total.toLocaleString()}</div>
        </div>
        <div className="card p-4">
          <div className="text-sm text-gray-500">Hoy</div>
          <div className="text-2xl font-bold text-blue-600">{stats.today}</div>
        </div>
        <div className="card p-4">
          <div className="text-sm text-gray-500">Warnings</div>
          <div className="text-2xl font-bold text-yellow-600">{stats.warnings}</div>
        </div>
        <div className="card p-4">
          <div className="text-sm text-gray-500">Criticos</div>
          <div className="text-2xl font-bold text-red-600">{stats.critical}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4 mb-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[150px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">Accion</label>
            <select
              value={filters.action}
              onChange={(e) => handleFilterChange('action', e.target.value)}
              className="input text-sm"
            >
              <option value="">Todas</option>
              {actions.map((action) => (
                <option key={action} value={action}>{action}</option>
              ))}
            </select>
          </div>

          <div className="flex-1 min-w-[150px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">Severidad</label>
            <select
              value={filters.severity}
              onChange={(e) => handleFilterChange('severity', e.target.value)}
              className="input text-sm"
            >
              <option value="">Todas</option>
              {severities.map((sev) => (
                <option key={sev} value={sev}>{sev}</option>
              ))}
            </select>
          </div>

          <div className="flex-1 min-w-[150px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">Staff</label>
            <select
              value={filters.staffId}
              onChange={(e) => handleFilterChange('staffId', e.target.value)}
              className="input text-sm"
            >
              <option value="">Todos</option>
              {staffList.map((s) => (
                <option key={s.id} value={s.id}>{s.nombre}</option>
              ))}
            </select>
          </div>

          <div className="flex-1 min-w-[150px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">Tipo Target</label>
            <input
              type="text"
              value={filters.targetType}
              onChange={(e) => handleFilterChange('targetType', e.target.value)}
              placeholder="ej: USER, HOUSEHOLD"
              className="input text-sm"
            />
          </div>

          <div className="flex-1 min-w-[140px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">Desde</label>
            <input
              type="date"
              value={filters.fromDate}
              onChange={(e) => handleFilterChange('fromDate', e.target.value)}
              className="input text-sm"
            />
          </div>

          <div className="flex-1 min-w-[140px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">Hasta</label>
            <input
              type="date"
              value={filters.toDate}
              onChange={(e) => handleFilterChange('toDate', e.target.value)}
              className="input text-sm"
            />
          </div>

          <button
            onClick={clearFilters}
            className="text-sm text-gray-500 hover:text-gray-700 px-3 py-2"
          >
            Limpiar
          </button>
        </div>
      </div>

      {/* Error */}
      {logsError && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          Error al cargar logs: {(logsError as Error).message}
        </div>
      )}

      {/* Table */}
      <div className="card overflow-hidden">
        {logsLoading ? (
          <div className="p-8 text-center text-gray-500">Cargando...</div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No se encontraron registros</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                      Fecha/Hora
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                      Staff
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                      Accion
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                      Target
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                      Severidad
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                      IP
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {logs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm">
                        <div className="font-medium text-gray-900">
                          {new Date(log.timestamp).toLocaleDateString('es-MX')}
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(log.timestamp).toLocaleTimeString('es-MX')}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-sm font-medium text-gray-900">
                          {log.staffNombre || 'Sistema'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {log.staffEmail || log.staffId}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          actionColors[log.action] || 'bg-gray-100 text-gray-800'
                        }`}>
                          {log.action}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm">
                        <div className="text-gray-900">{log.targetType}</div>
                        {log.targetId && (
                          <div className="text-xs text-gray-500 font-mono truncate max-w-[150px]" title={log.targetId}>
                            {log.targetId.substring(0, 8)}...
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          severityColors[log.severity] || 'bg-gray-100 text-gray-800'
                        }`}>
                          {log.severity}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-500">
                        {log.ipAddress || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
              <div className="text-sm text-gray-500">
                Mostrando {page * ITEMS_PER_PAGE + 1} - {Math.min((page + 1) * ITEMS_PER_PAGE, total)} de {total}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                <span className="px-3 py-1 text-sm">
                  {page + 1} / {totalPages || 1}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Siguiente
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
